import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

// Error message display duration - gives users time to read the message.
// Mirrors assets/product-form.js's and assets/routine-bundle.js's
// ERROR_MESSAGE_DISPLAY_DURATION. Duplicated rather than shared (see
// #showUpsellError below) — see that comment for why.
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts.
// Mirrors assets/product-form.js's and assets/routine-bundle.js's
// ERROR_BUTTON_REENABLE_DELAY.
const ERROR_BUTTON_REENABLE_DELAY = 1000;

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Per-button pending error-auto-hide timeouts, keyed by the clicked button
   * element. The "Complete the Routine" upsell row (snippets/cart-routine-upsell.liquid)
   * can render up to 2 independent "Add" buttons at once, each needing its own
   * disabled/error state — unlike assets/routine-bundle.js, which only ever has
   * one `addButton` ref for the whole bundle, there's no single shared button
   * here to key state off of.
   * @type {WeakMap<HTMLElement, number>}
   */
  #upsellErrorTimeouts = new WeakMap();

  /**
   * Variant IDs with an in-flight upsell `/cart/add.js` request, keyed by
   * `button.dataset.variantId`. This — not `button.disabled` — is the real
   * double-submit guard.
   *
   * Why: the cart-drawer section re-renders (morph) on ANY cart mutation
   * completing while a request here is in flight — e.g. a quantity-stepper
   * change elsewhere in the drawer, or a click on the *other* upsell button.
   * assets/morph.js only preserves `disabled` state for
   * `<input>`/`<option>`/`<textarea>` (via `updateInput`/
   * `syncFormControlState`); a `<button>`'s `disabled` attribute is
   * reconciled generically via `attributesEqual`/`copyAttributes` against
   * the freshly-fetched server markup, which knows nothing about this
   * client-side in-flight state and so has no `disabled` attribute to copy
   * over. Since the button node itself is preserved across the morph (same
   * tag/position), that re-render silently strips `disabled` off a button
   * whose request hasn't resolved yet — making it look clickable again and
   * letting a shopper fire a genuine duplicate `/cart/add.js` for the same
   * variant. Tracking in-flight variant IDs here, in JS memory morph cannot
   * touch, closes that gap; `button.disabled` is kept only as a secondary/
   * cosmetic affordance (harmless even though morph can clear it).
   * @type {Set<string>}
   */
  #upsellPendingVariantIds = new Set();

  /**
   * Fetches the current cart from the Cart API. Mirrors assets/routine-bundle.js's
   * `#fetchCart()` — the full cart JSON (not the `/cart/add.js` response, which
   * only echoes back the added line item, not cart-wide totals).
   */
  async #fetchUpsellCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Shows an inline error message next to a specific upsell "Add" button and
   * announces it via the row's shared live region, auto-hiding after
   * ERROR_MESSAGE_DISPLAY_DURATION. Keyed per-button (via #upsellErrorTimeouts)
   * so an error on one button doesn't cancel or clobber another button's
   * pending auto-hide. Mirrors assets/routine-bundle.js's #showError, adapted
   * for multiple independent buttons instead of a single ref.
   * @param {HTMLElement} button
   * @param {string} message
   */
  #showUpsellError(button, message) {
    const existingTimeout = this.#upsellErrorTimeouts.get(button);
    if (existingTimeout) clearTimeout(existingTimeout);

    const errorEl = button.closest('.cart-routine-upsell__item')?.nextElementSibling;

    if (errorEl instanceof HTMLElement && errorEl.classList.contains('cart-routine-upsell__error')) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');

      const timeout = window.setTimeout(() => {
        errorEl.classList.add('hidden');
        this.#upsellErrorTimeouts.delete(button);
      }, ERROR_MESSAGE_DISPLAY_DURATION);

      this.#upsellErrorTimeouts.set(button, timeout);
    }

    const liveRegion = this.querySelector('[data-upsell-live-region]');
    if (liveRegion) liveRegion.textContent = message;
  }

  /**
   * Handles a "Complete the Routine" upsell "Add" click (single-item
   * add-to-cart from within the drawer). Same CartLinesUpdateEvent shape as
   * assets/routine-bundle.js's `addAllToCart` (itself mirroring
   * assets/product-form.js:410-423) — a single options object, dispatched via
   * this.dispatchEvent, carrying a promise the listener awaits. Resolving
   * without a `sections` payload means component-cart-items.js's
   * `#handleCartUpdate` falls back to a full `sectionRenderer.renderSection`,
   * which re-renders this snippet from fresh cart state — that's what makes
   * the upsell row update (item removed from the row, or the whole row
   * disappearing) after a successful add, with no extra DOM surgery needed
   * here.
   *
   * Guards against double-submit by tracking the clicked button's variant ID
   * in `#upsellPendingVariantIds` for the duration of the request (bailing
   * early if that same variant is already in flight) — see that field's doc
   * for why this can't just be `button.disabled`. `button.disabled` is still
   * set for the duration of the request as a secondary/cosmetic affordance;
   * each button's guard is independent, since up to 2 buttons can render at
   * once, one per complementary product.
   *
   * Surfaces failures via CartErrorEvent + an inline message next to the
   * clicked button + the row's live region, mirroring
   * assets/routine-bundle.js's error-handling path — a shopper should never
   * see nothing happen with no explanation.
   *
   * On error, the deferred promise is *rejected* rather than resolved with
   * `{ didError: true }` (the shape assets/routine-bundle.js and
   * assets/product-form.js use). Both of this event's listeners
   * (component-cart-items.js's #handleCartUpdate and this file's own
   * #handleCartLinesUpdate) only run their refresh/settle logic in a
   * `.then()`, falling back to a silent `.catch()` on rejection. Resolving
   * (even with `didError: true`) would still make component-cart-items.js
   * fall into its no-`sections` branch and call
   * `sectionRenderer.renderSection('cart-drawer-section', ...)` — a full
   * re-render of this exact snippet from the server, which would immediately
   * overwrite the inline error text set by #showUpsellError below with a
   * fresh, error-free copy (confirmed via manual QA: the 422 response came
   * back correctly, but the inline error was gone by the time the section
   * finished refreshing a moment later). That's harmless for
   * routine-bundle.js, whose error UI lives on the PDP outside
   * `cart-drawer-section` entirely, but fatal here since this row *is*
   * inside that section. Nothing actually changed in the cart on error, so
   * there's nothing to refresh — rejecting is both more correct and avoids
   * this component clobbering its own error message.
   *
   * Not extracted into a shared module alongside assets/routine-bundle.js:
   * that component keys all of this state off a single `refs.addButton`,
   * while this one needs per-button state for up to 2 independently-clickable
   * buttons, so a shared helper would need a reworked, more generic API on
   * both sides. Given this is the third near-identical copy of this
   * error/live-region/double-submit pattern (after assets/product-form.js and
   * assets/routine-bundle.js), that extraction is worth doing, but doing it
   * now would mean modifying assets/routine-bundle.js's already-reviewed,
   * already-verified Task 2.4 code this late in the plan purely for a
   * refactor — judged not worth that risk here. Tracked as follow-up debt.
   *
   * @param {Event} event
   */
  async handleUpsellAdd(event) {
    const button =
      event.target instanceof Element ? event.target.closest('.cart-routine-upsell__add') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Already in-flight from a previous click for this variant — ignore rapid
    // repeat clicks. Checked before touching any DOM state: see
    // #upsellPendingVariantIds' doc for why `button.disabled` alone can't be
    // trusted as this guard (morph can silently clear it mid-request).
    if (this.#upsellPendingVariantIds.has(variantId)) return;
    this.#upsellPendingVariantIds.add(variantId);

    button.disabled = true;

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    /**
     * Handles both the fetch-level failure and the Cart API's in-band error
     * shape (a 200/422 response whose JSON body carries `status`/`message`).
     * Rejects `deferredEventPromise` — see the method doc above for why this
     * must reject rather than resolve.
     * @param {string} message
     * @param {{ description?: string, errors?: unknown }} [detail]
     */
    const handleFailure = (message, detail) => {
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
          detail,
        })
      );

      this.#showUpsellError(button, message);
      deferredEventPromise.reject(new Error(message));

      // Brief delay before re-enabling on error, mirroring
      // assets/routine-bundle.js's ERROR_BUTTON_REENABLE_DELAY — prevents a
      // shopper from immediately re-triggering the same failure.
      setTimeout(() => {
        button.disabled = false;
        this.#upsellPendingVariantIds.delete(variantId);
      }, ERROR_BUTTON_REENABLE_DELAY);
    };

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }) })
      );
      const result = await response.json();

      // Shopify's Cart API returns a `status` field (e.g. 422) in the JSON body on error.
      if (!response.ok || result.status) {
        handleFailure(result.message || 'Add to Bag failed', {
          description: result.description,
          errors: result.errors,
        });
        return;
      }
    } catch (error) {
      handleFailure('Add to Bag failed');
      return;
    }

    // Success path only from here — the add genuinely happened, so refresh
    // the drawer (via the resolved promise) to reflect it: the new line item
    // and this row losing the just-added product.
    try {
      const cart = await this.#fetchUpsellCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false },
      });
    } catch (error) {
      // The `/cart/add.js` call above already succeeded — the item IS in the
      // shopper's cart — so this is only the follow-up cart refresh failing.
      // Surface that distinction via the same inline error/live-region
      // mechanism as #showUpsellError, rather than leaving the shopper with
      // no signal that anything happened at all.
      this.#showUpsellError(
        button,
        "Added to cart, but we couldn't refresh your cart view — please reopen the cart."
      );
      deferredEventPromise.reject(error);
    } finally {
      button.disabled = false;
      this.#upsellPendingVariantIds.delete(variantId);
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
